'use client';

import { FC, useRef, useState } from 'react';
import { TextField } from '../components/atoms/text-field';
import { PhoneNumberInput } from '../components/PhoneNumberInput';
import { Button } from '../components/atoms/button';

import clsx from 'clsx';
import { getFontVariable, getRoundedClassName } from '../components/utils';
import {
  PageCollectionConfigFontFamily,
  PageCollectionConfigLanguage,
  PageCollectionConfigRadiusClassName,
  PageFieldsetFieldType,
  PageFieldsetFieldTypeEnum,
} from '@creator-suite/page-types';

import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  FIRST_NAME_MAX_LENGTH,
  getInsertLeadToPageSchemaByLanguage,
  InsertLeadToPageFormSchemaType,
  LAST_NAME_MAX_LENGTH,
} from '@/data/leads-dto';
import { getLabelForField, getPlaceholderForField } from '@/form-utils';
import { Checkbox } from './Checkbox';
import Link from 'next/link';
import { HaloContainer } from './HaloContainer';

export interface InsertLeadToPageFormProps {
  language: PageCollectionConfigLanguage;
  accountDisplayName: string;

  handleSubmit: (values: InsertLeadToPageFormSchemaType) => Promise<void>;
  onFormError: Parameters<
    ReturnType<typeof useForm<InsertLeadToPageFormSchemaType>>['handleSubmit']
  >[1];

  buttonRadiusClassName: PageCollectionConfigRadiusClassName;
  inputRadiusClassName: PageCollectionConfigRadiusClassName;
  cardRadiusClassName: PageCollectionConfigRadiusClassName;
  buttonText: string;
  fontFamily: PageCollectionConfigFontFamily;
  fieldset: PageFieldsetFieldType[];

  error?: string | null | undefined;
  termsUrl: string;
  state: 'initial' | 'loading' | 'success' | 'error';
  privacyPolicyUrl: string;
  backgroundUrl: string | null;
  backgroundColor: string | null;
  countryCode?: string;
}

export const ParticipateInLotteryForm: FC<InsertLeadToPageFormProps> = (
  props,
) => {
  const {
    language = PageCollectionConfigLanguage.DE,
    accountDisplayName,
    handleSubmit,
    onFormError,

    buttonRadiusClassName,
    inputRadiusClassName,
    buttonText,
    fontFamily,
    fieldset = [],
    error,
    termsUrl,
    state,
    privacyPolicyUrl,
    countryCode,
  } = props;

  const buttonRef = useRef<HTMLButtonElement>(null);

  const form = useForm<InsertLeadToPageFormSchemaType>({
    resolver: zodResolver(getInsertLeadToPageSchemaByLanguage(language)),
  });

  const {
    register,
    formState: { errors, isSubmitting },
  } = form;

  const fieldZip = fieldset.find(
    (field) => field.type === PageFieldsetFieldTypeEnum.ZIP,
  );
  const fieldEmail = fieldset.find(
    (field) => field.type === PageFieldsetFieldTypeEnum.EMAIL,
  );
  const fieldCity = fieldset.find(
    (field) => field.type === PageFieldsetFieldTypeEnum.CITY,
  );
  const fieldFirstName = fieldset.find(
    (field) => field.type === PageFieldsetFieldTypeEnum.FIRST_NAME,
  );
  const fieldLastName = fieldset.find(
    (field) => field.type === PageFieldsetFieldTypeEnum.LAST_NAME,
  );

  const onSubmit = async (data: InsertLeadToPageFormSchemaType) => {
    await handleSubmit(data);
  };

  const [acceptTac, setAcceptTac] = useState(false);

  return state === 'success' ? (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-center text-center">
        <p
          className={clsx(
            'text-center text-xl font-light tracking-tight',
            getFontVariable(fontFamily),
          )}
        >
          {language === 'de'
            ? 'Glückwunsch, Du nimmst an dem Gewinnspiel teil.'
            : 'Congratulations, you are participating in the lottery.'}
        </p>
      </div>
      <p
        className={clsx(
          'text-muted-foreground text-center text-xs font-light tracking-tight opacity-70',
          getFontVariable(fontFamily),
        )}
      >
        {language === 'de'
          ? 'Das Ende des Gewinnspiels wird über WhatsApp bekannt gegeben.'
          : 'The end of the lottery will be announced via WhatsApp.'}
      </p>
    </div>
  ) : (
    <form
      className="space-between flex flex-col space-y-8"
      onSubmit={form.handleSubmit(onSubmit, onFormError)}
    >
      <>
        <div className="flex grow flex-col space-y-4">
          {/* MOBILE PHONE FIELD */}
          <Controller
            name="phone"
            control={form.control}
            render={({ field }) => (
              <PhoneNumberInput
                label={getLabelForField(
                  PageFieldsetFieldTypeEnum.MOBILE_PHONE,
                  language,
                )}
                placeholder="+49 176 1234 1234"
                error={errors.phone?.message}
                className={getFontVariable(fontFamily)}
                roundedClassName={inputRadiusClassName}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
                countryCode={countryCode}
              />
            )}
          />
          {/* FIRST / LAST NAME FIELDS */}
          {fieldFirstName && fieldLastName && (
            <div className="flex items-center gap-4">
              <TextField
                type="text"
                placeholder={getPlaceholderForField(
                  fieldFirstName.type,
                  !fieldFirstName.required,
                  language,
                )}
                error={errors.firstName?.message}
                className={getFontVariable(fontFamily)}
                roundedClassName={inputRadiusClassName}
                maxLength={FIRST_NAME_MAX_LENGTH}
                {...register('firstName')}
              />
              <TextField
                type="text"
                placeholder={getPlaceholderForField(
                  fieldLastName.type,
                  !fieldLastName.required,
                  language,
                )}
                error={errors.lastName?.message}
                className={getFontVariable(fontFamily)}
                roundedClassName={inputRadiusClassName}
                maxLength={LAST_NAME_MAX_LENGTH}
                {...register('lastName')}
              />
            </div>
          )}
          {/* EMAIL FIELD */}
          {fieldEmail && (
            <TextField
              type="email"
              placeholder={getPlaceholderForField(
                fieldEmail.type,
                !fieldEmail.required,
                language,
              )}
              error={errors.email?.message}
              className={getFontVariable(fontFamily)}
              roundedClassName={inputRadiusClassName}
              {...register('email')}
            />
          )}
          {/* ZIP FIELD */}
          {fieldZip && (
            <TextField
              type="text"
              placeholder={getPlaceholderForField(
                fieldZip.type,
                !fieldZip.required,
                language,
              )}
              error={errors.zip?.message}
              className={getFontVariable(fontFamily)}
              roundedClassName={inputRadiusClassName}
              {...register('zip')}
            />
          )}
          {/* CITY FIELD */}
          {fieldCity && (
            <TextField
              type="text"
              placeholder={getPlaceholderForField(
                fieldCity.type,
                !fieldCity.required,
                language,
              )}
              error={errors.city?.message}
              className={getFontVariable(fontFamily)}
              roundedClassName={inputRadiusClassName}
              {...register('city')}
            />
          )}
          <Checkbox
            label={
              <span className="text-left text-[10px] leading-[15px] font-light">
                {language === 'de' ? (
                  <>
                    Hiermit akzeptiere ich die{' '}
                    <Link
                      href={termsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      Teilnahmebedingungen
                    </Link>{' '}
                    und bestätige, dass ich über 16 Jahre alt bin.
                  </>
                ) : (
                  <>
                    I hereby accept the{' '}
                    <Link
                      href={termsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      terms and conditions
                    </Link>
                    and confirm that I am over 18 years old.
                  </>
                )}
              </span>
            }
            checked={acceptTac}
            onChange={(e) => setAcceptTac(e.target.checked)}
          />
          <Checkbox
            {...register('consent')}
            label={
              <span className="text-left text-[10px] leading-[15px] font-light">
                {language === 'de' ? (
                  <>
                    Ich möchte das {accountDisplayName} neben dem Gewinnspiel
                    auch andere Nachrichten an mich senden kann für exklusive
                    Inhalte, Gewinnspiele & mehr. Weitere Infos zum Umgang mit
                    deinen Daten findest du unter{' '}
                    <Link
                      href={privacyPolicyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      Sicherheit und Datenschutz
                    </Link>
                    .
                  </>
                ) : (
                  <>
                    I would like {accountDisplayName} to send me other messages
                    besides the lottery for exclusive content, competitions &
                    more. For more information about how we handle your data,
                    please see{' '}
                    <Link
                      href={privacyPolicyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline"
                    >
                      Security and Privacy
                    </Link>
                    .
                  </>
                )}
              </span>
            }
          />
        </div>
      </>

      {error && (
        <div className="w-full text-center text-sm text-red-600">{error}</div>
      )}
      <HaloContainer
        elementRef={buttonRef as React.RefObject<HTMLElement>}
        disableAnimation={!acceptTac || isSubmitting || state === 'loading'}
      >
        <Button
          ref={buttonRef}
          type="submit"
          className={clsx(
            props.backgroundUrl ? 'bg-black!' : '',
            'animate-pulse',
            'mx-auto',
            getRoundedClassName(buttonRadiusClassName),
            getFontVariable(fontFamily),
          )}
          style={
            props.backgroundColor
              ? {
                  backgroundColor: props.backgroundColor,
                }
              : undefined
          }
          disabled={!acceptTac || isSubmitting || state === 'loading'}
        >
          <span className="leading-none text-current">{buttonText}</span>
        </Button>
      </HaloContainer>
    </form>
  );
};
